import React from 'react';
import { View, Text, StyleSheet } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  container: {
    marginBottom: 20,
  },
  sectionHeader: {
    fontSize: 10,
    fontWeight: 600,
    marginBottom: 5,
    color: '#333333',
  },
  subHeader: {
    fontSize: 7,
    color: '#666666',
    marginBottom: 8,
    lineHeight: 1.3,
  },
  table: {
    width: '100%',
  },
  tableHeader: {
    backgroundColor: '#f5f5f5',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  headerRow: {
    flexDirection: 'row',
  },
  headerCell: {
    fontSize: 7,
    fontWeight: 600,
    color: '#444444',
    padding: 4,
    textAlign: 'center',
    borderRightWidth: 1,
    borderRightColor: '#f0f0f0',
  },
  phaseHeaderCell: {
    fontSize: 7,
    fontWeight: 600,
    color: '#444444',
    padding: 4,
    textAlign: 'left',
    borderRightWidth: 1,
    borderRightColor: '#f0f0f0',
    flex: 0.2,
  },
  categoryHeaderCell: {
    fontSize: 7,
    fontWeight: 600,
    color: '#444444',
    padding: 4,
    textAlign: 'center',
    borderRightWidth: 1,
    borderRightColor: '#f0f0f0',
    flex: 0.15,
  },
  totalHeaderCell: {
    fontSize: 7,
    fontWeight: 600,
    color: '#444444',
    padding: 4,
    textAlign: 'center',
    flex: 0.15,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  summaryRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
    backgroundColor: '#f8f9fa',
  },
  phaseCell: {
    fontSize: 7,
    fontWeight: 600,
    padding: 4,
    borderRightWidth: 1,
    borderRightColor: '#f0f0f0',
    color: '#333333',
    flex: 0.2,
  },
  dataCell: {
    fontSize: 6,
    color: '#333333',
    padding: 4,
    textAlign: 'center',
    borderRightWidth: 1,
    borderRightColor: '#f0f0f0',
    flex: 0.15,
  },
  totalCell: {
    fontSize: 6,
    color: '#333333',
    padding: 4,
    textAlign: 'center',
    fontWeight: 600,
    flex: 0.15,
  },
  percentageText: {
    fontWeight: 600,
  },
  countText: {
    fontSize: 5,
    color: '#888',
  },
  emptyMessage: {
    fontSize: 8,
    color: '#888888',
    textAlign: 'center',
    padding: 10,
  }
});

// Category colors matching the cinema map
const categoryColors = {
  skills: '#4CAF50',
  knowledge: '#2196F3',
  mindset: '#FF9800',
  instruments: '#9C27B0',
};

const CategoryDistributionTablePDF = ({ cohortResponses, allCompetenciesData }) => {
  if (!cohortResponses?.competency_counts || !allCompetenciesData) {
    return (
      <View style={styles.container}>
        <Text style={styles.emptyMessage}>
          No data available for category distribution
        </Text>
      </View>
    );
  }

  // Create competency lookup map
  const competencyMap = new Map();
  allCompetenciesData.forEach(comp => {
    competencyMap.set(comp.id, {
      category: comp.category,
      phase: comp.abilities?.phase || 'Unknown'
    });
  });

  // Process data: group by phase and category  
  const phases = ['Empathize', 'Define', 'Ideate', 'Prototype', 'Test', 'Communicate'];
  const categories = ['skills', 'knowledge', 'mindset', 'instruments'];
  
  const distributionData = {};
  
  // Initialize data structure (phases as rows)
  phases.forEach(phase => {
    distributionData[phase] = {};
    categories.forEach(category => {
      distributionData[phase][category] = 0;
    });
    distributionData[phase].total = 0;
  });

  // Process competency counts
  cohortResponses.competency_counts.forEach(item => {
    const competencyDetails = competencyMap.get(item.competency_id);
    if (competencyDetails) {
      const { category, phase } = competencyDetails;
      if (distributionData[phase] && categories.includes(category)) {
        distributionData[phase][category] += item.count;
        distributionData[phase].total += item.count;
      }
    }
  });

  // Calculate totals for percentages
  const categoryTotals = {};
  categories.forEach(category => {
    categoryTotals[category] = phases.reduce((sum, phase) => 
      sum + distributionData[phase][category], 0);
  });
  
  const grandTotal = Object.values(categoryTotals).reduce((sum, total) => sum + total, 0);

  return (
    <View style={styles.container}>
      <Text style={styles.sectionHeader}>Category Distribution by Phase</Text>
      <Text style={styles.subHeader}>
        Shows how competency selections are distributed across categories within each phase. 
        Each percentage represents the proportion of total selections in that phase by category type.
        Selections are aggregated across all participants (non-unique, multiple participants can select the same competency).
      </Text>
      
      <View style={styles.table}>
        <View style={styles.tableHeader}>
          <View style={styles.headerRow}>
            <Text style={styles.phaseHeaderCell}>Phase</Text>
            <Text style={styles.categoryHeaderCell}>Skills</Text>
            <Text style={styles.categoryHeaderCell}>Knowledge</Text>
            <Text style={styles.categoryHeaderCell}>Mindset</Text>
            <Text style={styles.categoryHeaderCell}>Instruments</Text>
            <Text style={styles.totalHeaderCell}>Total Selections in Phase</Text>
          </View>
        </View>
        
        {phases.map(phase => {
          const phaseData = distributionData[phase];
          const phaseTotal = phaseData.total;
          
          return (
            <View key={phase} style={styles.tableRow}>
              <Text style={styles.phaseCell}>
                {phase}
              </Text>
              {categories.map(category => {
                const count = phaseData[category];
                const percentage = phaseTotal > 0 ? ((count / phaseTotal) * 100).toFixed(1) : '0.0';
                const categoryColor = categoryColors[category] || '#666666';
                
                return (
                  <View key={category} style={styles.dataCell}>
                    <Text style={{...styles.percentageText, color: categoryColor}}>
                      {percentage}%
                    </Text>
                    <Text style={styles.countText}>
                      ({count})
                    </Text>
                  </View>
                );
              })}
              <Text style={styles.totalCell}>
                {phaseTotal}
              </Text>
            </View>
          );
        })}
        
        {/* Summary row */}
        <View style={styles.summaryRow}>
           <Text style={{...styles.phaseCell, color: '#666', fontWeight: 600}}>
            Overall Distribution
          </Text>
          {categories.map(category => {
            const categoryTotal = categoryTotals[category];
            const percentage = grandTotal > 0 ? ((categoryTotal / grandTotal) * 100).toFixed(1) : '0.0';
            const categoryColor = categoryColors[category] || '#666666';
            
            return (
               <View key={category} style={{...styles.dataCell, fontWeight: 600}}>
                <Text style={{...styles.percentageText, color: categoryColor, fontWeight: 600}}>
                  {percentage}%
                </Text>
                <Text style={styles.countText}>
                  ({categoryTotal})
                </Text>
              </View>
            );
          })}
           <Text style={{...styles.totalCell, fontWeight: 600}}>
            {grandTotal}
          </Text>
        </View>
      </View>
    </View>
  );
};

export default CategoryDistributionTablePDF;
